<script setup lang="ts">
import { computed } from 'vue'

/**
 * A row of things with a gap between them — the horizontal half of `Stack`.
 *
 * Reach for it instead of writing `display: flex` plus a `gap` under a class whose only job
 * is to hold them. A column is `Stack`.
 *
 * Every default is CSS's own — items stretch, pack left and do not wrap — so a block
 * converted to this keeps its layout only if it states what its stylesheet used to state.
 */
const props = withDefaults(
  defineProps<{
    /** Gap between children: px as a number, or any CSS length ("4px 10px"). */
    gap?: number | string
    align?: 'stretch' | 'start' | 'center' | 'baseline' | 'end'
    justify?: 'start' | 'center' | 'between' | 'end'
    wrap?: boolean
    block?: boolean
    /**
     * Sit in a line of text rather than break one — `display: inline-flex`.
     *
     * For a row that is part of a sentence or of someone else's inline box: a form item's
     * label, where Element Plus draws the required asterisk as a `::before` on the label
     * itself, and a block-level row pushes that asterisk onto a line of its own. Spelled
     * the same way as `Hint`'s `inline`.
     */
    inline?: boolean
  }>(),
  {
    gap: 0,
    align: 'stretch',
    justify: 'start',
    wrap: false,
    block: false,
    inline: false
  }
)

const ALIGN = {
  stretch: 'stretch',
  start: 'flex-start',
  center: 'center',
  baseline: 'baseline',
  end: 'flex-end'
}

const JUSTIFY = {
  start: 'flex-start',
  center: 'center',
  between: 'space-between',
  end: 'flex-end'
}

const style = computed(() => ({
  gap: typeof props.gap === 'number' ? `${props.gap}px` : props.gap,
  alignItems: ALIGN[props.align],
  justifyContent: JUSTIFY[props.justify],
  flexWrap: props.wrap ? ('wrap' as const) : ('nowrap' as const),
  width: props.block ? '100%' : undefined
}))
</script>

<template>
  <div
    class="shared-inline"
    :class="{ 'shared-inline--inline': inline }"
    :style="style"
  >
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.shared-inline {
  display: flex;
}

.shared-inline--inline {
  display: inline-flex;
  vertical-align: baseline;
}

/* Element Plus gives every button after the first a 12px left margin — a block setting its
   own outer spacing, which is the row's job and nobody else's. Left in, a row asking for
   `gap: 8` draws 20 between its buttons and a wrapped line starts indented by 12, and the
   caller has no way to say otherwise: `:deep(.el-…)` belongs here, not in a slice.

   Direct children only, so a row of buttons that some nested component lays out itself is
   left alone. */
.shared-inline > :deep(.el-button + .el-button) {
  margin-left: 0;
}
</style>
